import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ResolveByUuidParameter,
  ResolveByUuidResultFactory,
  UseOptionsDataSource,
  UseResolveMethod,
} from 'eurogard-form-system';
import {
  FormType,
  RxapForm,
  RxapFormControl,
  RxapFormGroup,
  RxapValidators,
  UseFormControl,
} from 'eurogard-forms';
import { ToOptions } from '@rxap/utilities';
import {
  UseTableSelectColumns,
  UseTableSelectDataSource,
  UseTableSelectToDisplay,
  UseTableSelectToValue,
} from 'angular-components/table-select/decorators';
import { CompanyGuiTableDataSource } from 'open-api-table-data-source/data-sources/company-gui-table.data-source';
import {
  AutocompleteOptionsGetByFilterParameterAdapterFactory,
  AutocompleteOptionsGetByFilterResultAdapterFactory,
  UseAutocompleteOptionsMethod,
} from 'autocomplete-table-select';
import {
  CompanyGuiControllerGetByFilterRemoteMethod,
} from 'open-api-legacy/remote-methods/company-gui-controller-get-by-filter.remote-method';
import { CompanyDetailsResponse } from 'open-api-legacy/responses/company-details.response';
import {
  CompanyControllerGetByUuidRemoteMethod,
} from 'open-api-legacy/remote-methods/company-controller-get-by-uuid.remote-method';
import { CompanyControllerGetByUuidResponse } from 'open-api-legacy/responses/company-controller-get-by-uuid.response';
import { uuidValidator } from 'angular-forms/validators/is-valid-uuid';
import { CountryOptionsDataSource } from 'angular-data-sources/country-options.data-source';

export interface IUserSettingsEditForm {
  isSelf: boolean;
  admin: boolean;
  city: string;
  companyUuid: string;
  country: any;
  firstname: string;
  lastname: string;
  role: any;
  phone: string;
  street: string;
  username: string;
  zip: string;
  restrictedAccess: boolean;
}

@Injectable()
@RxapForm('user-edit')
export class UserSettingsEditForm implements FormType<IUserSettingsEditForm> {
  public rxapFormGroup!: RxapFormGroup<IUserSettingsEditForm>;

  @UseFormControl({
    state: false,
  })
  public isSelf!: RxapFormControl<boolean>;

  @UseFormControl({})
  public admin!: RxapFormControl<boolean>;

  @UseFormControl({
    validators: [ RxapValidators.IsString() ],
  })
  public city!: RxapFormControl<string>;

  @UseTableSelectColumns({name: {label: 'Name', filter: true}})
  @UseTableSelectToDisplay(company => company['name'])
  @UseTableSelectToValue(company => company['uuid'])
  @UseTableSelectDataSource(CompanyGuiTableDataSource)
  @UseAutocompleteOptionsMethod(CompanyGuiControllerGetByFilterRemoteMethod, {
    adapter: {
      parameter: AutocompleteOptionsGetByFilterParameterAdapterFactory(),
      result: AutocompleteOptionsGetByFilterResultAdapterFactory<CompanyDetailsResponse>(
        items =>
          ToOptions(
            items,
            item => item.uuid,
            item => item.name!,
          ),
      ),
    },
  })
  @UseResolveMethod(CompanyControllerGetByUuidRemoteMethod, {
    adapter: {
      parameter: ResolveByUuidParameter,
      result: ResolveByUuidResultFactory<CompanyControllerGetByUuidResponse>(
        item => ({value: item.uuid, display: item.name}),
      ),
    },
  })
  @UseFormControl({
    validators: [ Validators.required, uuidValidator as any ],
  })
  public companyUuid!: RxapFormControl<string>;

  @UseOptionsDataSource(CountryOptionsDataSource)
  @UseFormControl({})
  public country!: RxapFormControl<string>;

  @UseFormControl({
    validators: [ RxapValidators.IsString() ],
  })
  public firstname!: RxapFormControl<string>;

  @UseFormControl({
    validators: [ RxapValidators.IsString() ],
  })
  public lastname!: RxapFormControl<string>;

  @UseFormControl({
    validators: [ Validators.required ],
  })
  public role!: RxapFormControl<string>;

  @UseFormControl({
    validators: [ RxapValidators.IsString() ],
  })
  public phone!: RxapFormControl<string>;

  @UseFormControl({
    validators: [ RxapValidators.IsString() ],
  })
  public street!: RxapFormControl<string>;

  @UseFormControl({
    validators: [
      Validators.required,
      Validators.pattern(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,10}$/),
      Validators.maxLength(50),
      RxapValidators.IsString(),
    ],
  })
  public username!: RxapFormControl<string>;

  @UseFormControl({
    validators: [ RxapValidators.IsString() ],
  })
  public zip!: RxapFormControl<string>;

  @UseFormControl({
    state: false,
  })
  public restrictedAccess!: RxapFormControl<boolean>;

  rxapOnInit(): void {
    if (this.isSelf.value) {
      this.restrictedAccess.disable();
    }
  }

  toJSON() {
    return {
      username: this.rxapFormGroup.value.username,

      firstname: this.rxapFormGroup.value.firstname,
      lastname: this.rxapFormGroup.value.lastname,

      street: this.rxapFormGroup.value.street,
      phone: this.rxapFormGroup.value.phone,

      zip: this.rxapFormGroup.value.zip,
      city: this.rxapFormGroup.value.city,
      country: this.rxapFormGroup.value.country,

      restrictedAccess: this.rxapFormGroup.value.restrictedAccess,

      admin: this.rxapFormGroup.value.admin,
    }
  }
}
